<template>
  <div :style="styleObject">
    <button
      style="display: none"
      type="button"
      class="btn-show-modal-confirm-still-there"
      @click="show"
    ></button>
    <button
      style="display: none"
      type="button"
      class="btn-hide-modal-confirm-still-there"
      @click="hide"
    ></button>
    <div v-if="visible" class="ksk-still">
      <div class="ksk-still__popup">
        <div class="ksk-still__ring">
          <svg viewBox="0 0 240 240" aria-hidden="true">
            <circle
              class="ksk-still__track"
              cx="120"
              cy="120"
              r="104"
              fill="transparent"
              stroke-width="16"
            ></circle>
            <circle
              class="ksk-still__progress"
              cx="120"
              cy="120"
              r="104"
              fill="transparent"
              stroke-width="16"
              stroke-linecap="round"
              :stroke-dasharray="ringLength"
              :stroke-dashoffset="ringOffset"
            ></circle>
          </svg>
          <span class="ksk-still__time">{{ clockText }}</span>
        </div>
        <h2 class="ksk-still__title">{{ $t("kiosk.stillThere.title") }}</h2>
        <p class="ksk-still__desc">
          {{ $t("kiosk.stillThere.desc") }}
        </p>
        <button
          type="button"
          @click="continueOrder()"
          class="ksk-still__continue"
        >
          <span>{{ $t("kiosk.common.continue") }}</span>
          <span class="material-icons-round">arrow_forward</span>
        </button>
        <button type="button" @click="resetNowClick()" class="ksk-still__reset">
          {{ $t("kiosk.stillThere.cancelOrder") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ModalConfirmStillThere",
  props: {
    keyReload: {
      required: true,
    },
    continueMyOrder: {
      type: Function,
      default: () => {},
    },
    resetNow: {
      type: Function,
      default: () => {},
    },
  },
  computed: {
    styleObject() {
      return {
        "--color": this.$store.state.color,
      };
    },
    // KSK026 — display only: m:ss text and the ring arc for the countdown.
    clockText() {
      const t = Math.max(this.timeLeft, 0);
      return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, "0")}`;
    },
    ringLength() {
      return 2 * Math.PI * 104;
    },
    ringOffset() {
      return this.ringLength * (1 - Math.max(this.timeLeft, 0) / 60);
    },
  },
  data() {
    return {
      visible: false,
      timeLeft: 60,
      displayTime: "60",
      timer: null,
    };
  },
  methods: {
    show() {
      this.timeLeft = 60;
      this.displayTime = "60";
      clearInterval(this.timer);
      this.timer = null;
      this.visible = true;
      this.startCountdown();
    },

    hide() {
      clearInterval(this.timer);
      this.timer = null;
      this.visible = false;
    },

    startCountdown() {
      this.timer = setInterval(() => {
        if (this.timeLeft > 0) {
          this.timeLeft--;
          this.displayTime = this.timeLeft < 10 ? `0${this.timeLeft}` : this.timeLeft;
        } else {
          clearInterval(this.timer);
          this.timer = null;
          this.resetNowClick();
        }
      }, 1000);
    },

    continueOrder() {
      this.hide();
      this.continueMyOrder();
    },

    resetNowClick() {
      this.hide();
      this.resetNow();
    },
  },
  watch: {
    keyReload() {
      this.show();
    },
  },
  beforeUnmount() {
    clearInterval(this.timer);
    this.timer = null;
  },
};
</script>

<style scoped>
/* KSK026 — P9 "Are you still there?" (kiosk theme 2). Kiosk-only component;
   tokens come from kiosk-theme2.css (var(--ksk-*)). */
.ksk-still {
  position: fixed;
  inset: 0;
  z-index: 9999999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--ksk-gutter);
  background: rgba(10, 20, 10, 0.6);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}
.ksk-still__popup {
  width: 600px;
  max-width: 100%;
  max-height: calc(var(--ksk-vh, 1vh) * 92);
  overflow-y: auto;
  padding: 40px;
  border-radius: var(--ksk-radius-popup);
  background: var(--ksk-surface);
  box-shadow: 0 24px 64px rgba(20, 26, 20, 0.25);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  font-family: var(--ksk-font);
  color: var(--ksk-text);
}

/* Countdown ring */
.ksk-still__ring {
  position: relative;
  width: 176px;
  height: 176px;
  flex-shrink: 0;
}
.ksk-still__ring svg {
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
}
.ksk-still__track {
  stroke: var(--ksk-surface-muted);
}
.ksk-still__progress {
  stroke: var(--ksk-primary);
  transition: stroke-dashoffset 1s linear;
}
.ksk-still__time {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 52px;
  line-height: 1;
  font-weight: 800;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
  color: var(--ksk-primary);
}

.ksk-still__title {
  margin: var(--ksk-space-3) 0 0;
  font-family: inherit;
  font-size: var(--ksk-title-size);
  line-height: var(--ksk-title-lh);
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--ksk-text);
}
.ksk-still__desc {
  margin: var(--ksk-space-1) 0 0;
  max-width: 480px;
  font-size: 20px;
  line-height: 28px;
  color: var(--ksk-text-secondary);
}

/* Actions */
.ksk-still__continue {
  margin-top: var(--ksk-space-4);
  width: 100%;
  height: 80px;
  border: none;
  border-radius: var(--ksk-radius-pill);
  background: var(--ksk-primary);
  color: var(--ksk-on-primary);
  font-family: inherit;
  font-size: var(--ksk-label-size);
  line-height: var(--ksk-label-lh);
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  box-shadow: var(--ksk-shadow-float);
  transition: transform 0.1s ease, background-color 0.1s ease;
}
.ksk-still__continue .material-icons-round {
  font-size: 28px;
}
.ksk-still__continue:active {
  transform: scale(0.98);
  background: var(--ksk-primary-pressed);
}
.ksk-still__reset {
  margin-top: var(--ksk-space-1);
  min-height: 64px;
  padding: 0 var(--ksk-space-4);
  border: none;
  background: transparent;
  color: var(--ksk-text-secondary);
  font-family: inherit;
  font-size: var(--ksk-label-size);
  line-height: var(--ksk-label-lh);
  font-weight: 600;
}
.ksk-still__reset:active {
  color: var(--ksk-error);
}
</style>
